import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageProvider";

const REST_PRESETS = [60, 90, 120];
const CIRCUMFERENCE = 2 * Math.PI * 44;

interface RestParticle {
  id: number;
  left: string;
  top: string;
  size: number;
  color: string;
  dur: number;
  delay: number;
  yDrift: number;
}

const REST_PARTICLES: RestParticle[] = [
  { id: 0, left: "15%", top: "25%", size: 2, color: "#00e5ff", dur: 9, delay: 0, yDrift: -12 },
  { id: 1, left: "80%", top: "40%", size: 1.5, color: "hsl(265 60% 65%)", dur: 11, delay: 1, yDrift: -10 },
  { id: 2, left: "50%", top: "70%", size: 2, color: "#00e5ff", dur: 10, delay: 0.5, yDrift: -14 },
  { id: 3, left: "30%", top: "55%", size: 1.8, color: "hsl(265 60% 65%)", dur: 12, delay: 2, yDrift: -11 },
];

interface WorkoutRestTimerProps {
  defaultDuration: number;
  onComplete: () => void;
  onSkip: () => void;
  deadline?: number | null;
  onDeadlineChange?: (deadline: number, duration: number) => void;
  controlledRemainingMs?: number;
}

const WorkoutRestTimer: React.FC<WorkoutRestTimerProps> = ({ defaultDuration, onComplete, onSkip, deadline, onDeadlineChange, controlledRemainingMs }) => {
  const { t } = useLanguage();
  const reducedMotion = useReducedMotion();
  const [duration, setDuration] = useState(Math.max(0, defaultDuration));
  const deadlineRef = useRef(deadline ?? Date.now() + Math.max(0, defaultDuration) * 1000);
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
  const finished = useRef(false);
  const callbacks = useRef({ onComplete, onSkip, onDeadlineChange });
  callbacks.current = { onComplete, onSkip, onDeadlineChange };

  useEffect(() => {
    if (controlledRemainingMs !== undefined) return;
    if (deadline != null) deadlineRef.current = deadline;
    else callbacks.current.onDeadlineChange?.(deadlineRef.current, duration);
    const tick = () => {
      if (finished.current) return;
      const seconds = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setRemaining(seconds);
      if (!seconds) {
        finished.current = true;
        try { navigator.vibrate?.(200); } catch {}
        callbacks.current.onComplete();
      }
    };
    tick();
    const timer = setInterval(tick, 250);
    document.addEventListener("visibilitychange", tick); window.addEventListener("focus", tick);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", tick); window.removeEventListener("focus", tick); };
  }, [deadline, duration, controlledRemainingMs]);

  const changeDuration = (d: number) => {
    if (finished.current) return;
    deadlineRef.current = Date.now() + d * 1000;
    setDuration(d); setRemaining(d);
    callbacks.current.onDeadlineChange?.(deadlineRef.current, d);
  };
  const skip = () => {
    if (finished.current) return;
    finished.current = true; callbacks.current.onSkip();
  };

  const shownRemaining = controlledRemainingMs === undefined ? remaining : Math.ceil(Math.max(0, controlledRemainingMs) / 1000);
  const offset = CIRCUMFERENCE * (1 - shownRemaining / Math.max(1, duration));

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{ background: "rgba(5,8,16,0.94)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Rest particles */}
      {!reducedMotion && REST_PARTICLES.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-full pointer-events-none"
          style={{ width: p.size, height: p.size, background: p.color, opacity: 0.15, left: p.left, top: p.top }}
          animate={{ y: [0, p.yDrift, 0], opacity: [0.12, 0.2, 0.12] }}
          transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }}
        />
      ))}

      {/* Breathing glow */}
      <motion.div
        className="absolute w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.04) 0%, transparent 70%)" }}
        animate={reducedMotion ? {} : { opacity: [0.3, 0.6, 0.3], scale: [0.95, 1.08, 0.95] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-6 relative z-10"
        style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.workout.restTimer")}</p>

      {/* SVG circle timer */}
      <div className="relative w-[160px] h-[160px] mb-6 z-10">
        <svg width="160" height="160" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="44" fill="none" stroke="hsl(222 18% 15%)" strokeWidth="3" />
          <circle
            cx="50" cy="50" r="44" fill="none"
            stroke="hsl(187 72% 50%)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            transform="rotate(-90 50 50)"
            style={{ transition: "stroke-dashoffset 1s linear" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.span
            className="font-display text-[52px] font-bold text-white"
            animate={reducedMotion ? {} : { scale: [1, 1.02, 1] }}
            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
          >
            {shownRemaining}
          </motion.span>
        </div>
      </div>

      {/* Duration presets */}
      <div className="flex gap-2 mb-6 relative z-10">
        {REST_PRESETS.map(d => (
          <button
            key={d}
            type="button" aria-pressed={duration === d}
            onClick={() => changeDuration(d)}
            className="px-4 py-2 rounded-xl font-display text-[12px] font-semibold transition-all"
            style={{
              background: duration === d ? "hsl(187 72% 50% / 0.15)" : "transparent",
              border: `1px solid hsl(187 72% 50% / ${duration === d ? "0.40" : "0.15"})`,
              color: "hsl(187 72% 55%)",
            }}
          >
            {d}s
          </button>
        ))}
      </div>

      {/* Skip button */}
      <button
        onClick={skip}
        className="px-8 py-3.5 rounded-xl font-display text-[14px] font-semibold relative z-10 active:scale-95 transition-transform"
        style={{
          background: "hsl(187 72% 50% / 0.12)",
          border: "1px solid hsl(187 72% 50% / 0.30)",
          color: "hsl(187 72% 60%)",
          boxShadow: "0 0 16px hsl(187 72% 50% / 0.08)",
        }}
      >
        {t("fitness.workout.skipRest")}
      </button>

      {/* Mirror tip placeholder */}
      <div className="mt-8 px-6 relative z-10">
        <p className="text-[10px] font-body text-center italic" style={{ color: "hsl(218 14% 40%)" }}>
          {/* Future: Mirror tip during rest */}
        </p>
      </div>
    </motion.div>
  );
};

export default React.memo(WorkoutRestTimer);
